import { useCallback, useMemo } from 'react';
import { Formik, getIn, setIn } from 'formik';
import { Button } from '@mui/material';
import Card from '../../../../common/card';
import Input from '../../../../common/input';
import { Autocomplete } from '../../../../common/autocomplete';
import SelectMenu from '../../../../common/select-menu';
import Loading from '../../../../common/loading';
import MessageError from '../../../../common/message-error';
import l from '../../../../../lang';
import { CreatePayoutData, FlowType, PurposeOfPayout, RequiredField } from '../../../../../store/payouts/interfaces';
import {
  getCreatePayoutDataValueByFieldPath,
  getFieldLabel,
  payoutPurposeOptions,
  recordToSelectMenuOptions,
} from '../utils';
import { ErrorInfo, invalidBankAccountCode, invalidDocumentCode } from '../../../../../utils/error-codes';
import { ReactComponent as BriefcaseIcon } from '../../../../../assets/icons/briefcase.svg';
import { operatingCountries } from '../../../../../config';
import PayoutCurrencySelect from '../payout-data/payout-currency-select';

interface BankAccountFormProps {
  loading: boolean;
  validating: boolean;
  fields: RequiredField[];
  bankOptions: Array<{ code: string; name: string }>;
  initialValues: Partial<CreatePayoutData>;
  initialFieldValues?: Record<string, string | number>;
  country: string;
  flowType: FlowType;
  currencyOptions?: Record<string, string>;
  merchantCountry?: string;
  documentTypesForCompany: Record<string, string>;
  documentTypesForNaturalPerson: Record<string, string>;
  relationshipOptions?: Record<string, string>;
  errorInfo?: ErrorInfo;
  validationError?: boolean;
  backendErrorFields?: Record<string, string>;
  submitLabel?: string;
  accountNumberDisabled?: boolean;
  existingContactEmails?: string[];
  onBack: () => void;
  onSubmit: (values: Record<string, string | number>) => void;
}

type SelectOption = {
  value: string | null;
  label: string;
};

const RELATIONSHIP_FIELD_PATH = 'beneficiary.relationship';

const relationshipField: RequiredField = {
  fieldPath: RELATIONSHIP_FIELD_PATH,
  required: true,
  dataType: 'SELECT',
  description: 'Beneficiary relationship',
  displayOrder: 135,
};

/**
 * Computes backend field-level error message for a specific field based on errorInfo.
 * Returns the error message string if the field has a backend error, empty string otherwise.
 */
const getBackendFieldError = (fieldPath: string, errorInfo: { code?: number; message?: string } | undefined): string => {
  if (!errorInfo) return '';

  if (errorInfo.code === invalidDocumentCode && fieldPath === 'beneficiary.document.id' && errorInfo.message) {
    return l(errorInfo.message);
  }

  if (errorInfo.code === invalidBankAccountCode && fieldPath === 'beneficiary.bank_account.account' && errorInfo.message) {
    return l(errorInfo.message);
  }

  return '';
};

const bankAccountTypeOptionsMap: Record<string, SelectOption[]> = {
  AR: [
    {
      value: 'ALIAS',
      label: 'ALIAS',
    },
    {
      value: 'CBU',
      label: 'CBU/CVU',
    },
  ],
  BO: [],
  BR: [
    {
      value: 'CHECKING',
      label: 'Checking',
    },
    {
      value: 'SAVINGS',
      label: 'Savings',
    },
  ],
  CL: [
    {
      value: 'CHECKING',
      label: 'Checking',
    },
    {
      value: 'SAVINGS',
      label: 'Savings',
    },
    {
      value: 'VISTA',
      label: 'Vista',
    },
  ],
  CO: [
    {
      value: 'CHECKING',
      label: 'Checking',
    },
    {
      value: 'SAVINGS',
      label: 'Savings',
    },
  ],
  DO: [
    {
      value: 'CHECKING',
      label: 'Checking',
    },
    {
      value: 'SAVINGS',
      label: 'Savings',
    },
  ],
  EC: [
    {
      value: 'CHECKING',
      label: 'Checking',
    },
    {
      value: 'SAVINGS',
      label: 'Savings',
    },
  ],
  CR: [],
  MX: [
    {
      value: 'CHECKING',
      label: 'Checking',
    },
    {
      value: 'SAVINGS',
      label: 'Savings',
    },
  ],
  PE: [
    {
      value: 'CHECKING',
      label: 'Checking',
    },
    {
      value: 'SAVINGS',
      label: 'Savings',
    },
    {
      value: 'MAESTRA',
      label: 'Maestra',
    },
  ],
  PY: [
    {
      value: 'CHECKING',
      label: 'Checking',
    },
    {
      value: 'SAVINGS',
      label: 'Savings',
    },
  ],
  UY: [
    {
      value: 'CHECKING',
      label: 'Checking',
    },
    {
      value: 'SAVINGS',
      label: 'Savings',
    },
  ],
};

const BankAccountForm = ({
  loading,
  validating,
  fields,
  bankOptions,
  initialValues,
  initialFieldValues = {},
  country,
  flowType,
  currencyOptions = {},
  merchantCountry,
  documentTypesForCompany,
  documentTypesForNaturalPerson,
  relationshipOptions = {},
  errorInfo,
  validationError = false,
  backendErrorFields,
  submitLabel = l('common.next'),
  accountNumberDisabled = false,
  existingContactEmails = [],
  onBack,
  onSubmit,
}: BankAccountFormProps) => {
  const getDefaultValue = useCallback(
    (field: RequiredField) => {
      if (field.fieldPath === 'remitter.nationality') {
        return merchantCountry ?? '';
      }

      if (
        field.fieldPath === 'purpose' ||
        field.fieldPath === 'beneficiary.purpose'
      ) {
        return PurposeOfPayout.OTHER_SERVICES;
      }

      if (field.fieldPath === 'beneficiary.document.type' && country === 'CL') {
        return 'RUT';
      }

      if (
        field.fieldPath === 'currency_to_pay' ||
        field.fieldPath === 'beneficiary.bank_account.currency'
      ) {
        return Object.keys(currencyOptions)[0] ?? '';
      }

      return '';
    },
    [merchantCountry, country, currencyOptions]
  );

  const relationshipSelectOptions = useMemo(
    () => recordToSelectMenuOptions(relationshipOptions),
    [relationshipOptions]
  );

  const formFields = useMemo(() => {
    if (relationshipSelectOptions.length === 0) {
      return fields;
    }

    const withoutDuplicateRelationship = fields.filter(
      field => field.fieldPath !== RELATIONSHIP_FIELD_PATH && field.fieldPath !== 'relationship'
    );
    const emailIndex = withoutDuplicateRelationship.findIndex(field => field.fieldPath === 'beneficiary.email');
    const nextFields = [...withoutDuplicateRelationship];

    if (emailIndex >= 0) {
      nextFields.splice(emailIndex + 1, 0, relationshipField);
    } else {
      nextFields.push(relationshipField);
    }

    return nextFields;
  }, [fields, relationshipSelectOptions.length]);

  const formInitialValues = useMemo(() => {
    return formFields.reduce<Record<string, any>>((accumulator, field) => {
      const fieldValue =
        initialFieldValues[field.fieldPath] ?? getIn(initialFieldValues, field.fieldPath);
      const mappedValue = fieldValue ?? getCreatePayoutDataValueByFieldPath(initialValues, field.fieldPath);
      const isCurrencyField =
        field.fieldPath === 'currency_to_pay' ||
        field.fieldPath === 'beneficiary.bank_account.currency';
      const hasValidMappedCurrency =
        !isCurrencyField ||
        (mappedValue !== undefined && Boolean(currencyOptions[String(mappedValue)]));
      const nextValue = hasValidMappedCurrency
        ? mappedValue ?? getDefaultValue(field)
        : getDefaultValue(field);
      return setIn(accumulator, field.fieldPath, nextValue);
    }, {});
  }, [currencyOptions, formFields, getDefaultValue, initialFieldValues, initialValues]);

  const initialErrors = useMemo(() => {
    if (!errorInfo) {
      return {};
    }

    if (errorInfo.code === invalidDocumentCode) {
      return {
        'beneficiary.document.id': l(errorInfo.message),
      };
    }

    if (errorInfo.code === invalidBankAccountCode) {
      return {
        'beneficiary.bank_account.account': l(errorInfo.message),
      };
    }
  }, [errorInfo]);

  const documentTypes = useMemo(() => {
    if (country === 'CL') {
      return { RUT: 'RUT' };
    }

    if (flowType === FlowType.B2B) {
      return documentTypesForCompany;
    }

    return documentTypesForNaturalPerson;
  }, [
    flowType,
    country,
    documentTypesForCompany,
    documentTypesForNaturalPerson,
  ]);

  const allCountriesOptions = useMemo(() => {
    return operatingCountries.reduce<Record<string, string>>((accumulator, country) => {
      accumulator[country] = l(`countryCode.${country}`);
      return accumulator;
    }, {});
  }, []);

  const bankAutocompleteOptions = useMemo(
    () => bankOptions.map(({ code, name }) => ({ label: name, value: String(code) })),
    [bankOptions]
  );

  const bankAccountTypeSelectOptions = useMemo(() => {
    const options = bankAccountTypeOptionsMap[country] ?? ([] as SelectOption[]);
    return options.map(option => ({ value: option.value ?? '', label: option.label }));
  }, [country]);

  const documentTypeSelectOptions = useMemo(() => recordToSelectMenuOptions(documentTypes), [documentTypes]);

  const countrySelectOptions = useMemo(() => recordToSelectMenuOptions(allCountriesOptions), [allCountriesOptions]);

  const displayFields = useMemo(() => {
    const isCurrencyField = (field: RequiredField) =>
      field.fieldPath === 'currency_to_pay' ||
      field.fieldPath === 'beneficiary.bank_account.currency';

    return [
      ...formFields.filter(field => !isCurrencyField(field)),
      ...formFields.filter(isCurrencyField),
    ];
  }, [formFields]);

  const existingEmails = useMemo(
    () => new Set(existingContactEmails.map(email => email.trim().toLowerCase()).filter(Boolean)),
    [existingContactEmails]
  );

  const validate = (values: Record<string, any>) => {
    const errors: Record<string, string> = {};

    formFields.forEach(field => {
      const value = getIn(values, field.fieldPath);
      const textValue = value === undefined || value === null ? '' : String(value).trim();

      if (field.required && !textValue) {
        errors[field.fieldPath] = l('form.input.required');
        return;
      }

      if (field.maxLength && textValue.length > field.maxLength) {
        errors[field.fieldPath] = l('payouts.create.error.maxLength', field.maxLength);
        return;
      }

      if (
        textValue &&
        (field.dataType === 'EMAIL' || field.fieldPath === 'beneficiary.email') &&
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(textValue)
      ) {
        errors[field.fieldPath] = l('form.input.invalid');
        return;
      }

      if (
        textValue &&
        field.fieldPath === 'beneficiary.email' &&
        existingEmails.has(textValue.toLowerCase())
      ) {
        errors[field.fieldPath] = l('payoutContacts.create.error.emailAlreadyExists');
      }
    });

    return errors;
  };

  return (
    <div className="payout-create__layout">
      <Card>
        <div className="payout-create__card-content">
          <h2>{l('payouts.create.accountData.bankDataTitle')}</h2>
          <br />
          <div className="transaction_detail__title_section">
            <BriefcaseIcon className="transaction_detail__title_section__icon" />
            {l('payouts.create.accountData.bankData')}
          </div>

          {validationError &&
            errorInfo &&
            errorInfo.code !== invalidDocumentCode &&
            errorInfo.code !== invalidBankAccountCode && <MessageError errorInfo={errorInfo} />}

          <Formik
            initialValues={formInitialValues}
            validate={validate}
            initialErrors={initialErrors}
            enableReinitialize
            onSubmit={values => {
              const normalizedValues = formFields.reduce<Record<string, string | number>>((accumulator, field) => {
                const value = getIn(values, field.fieldPath);
                if (value !== undefined) {
                  accumulator[field.fieldPath] = value;
                }
                return accumulator;
              }, {});
              onSubmit(normalizedValues);
            }}
          >
            {({ values, errors, touched, handleBlur, setFieldValue, handleSubmit }) => (
              <form className="payout-create__account-data-form" onSubmit={handleSubmit}>
                {(loading || validating) && (
                  <div className="payout-create__loading">
                    <Loading />
                  </div>
                )}
                {!loading &&
                  !validating &&
                  displayFields.map(field => {
                    const label = getFieldLabel(field);
                    const fieldValue = getIn(values, field.fieldPath) || '';
                    const clientError = errors[field.fieldPath];
                    const hasBackendError = backendErrorFields?.[field.fieldPath] !== undefined;
                    const hasCurrentBackendError = hasBackendError
                      ? backendErrorFields?.[field.fieldPath] === String(fieldValue)
                      : false;
                    const backendFieldError = getBackendFieldError(field.fieldPath, errorInfo);
                    const error =
                      clientError || backendFieldError || (hasCurrentBackendError ? l('form.input.invalid') : '');
                    const showError =
                      backendFieldError || hasCurrentBackendError ? error : getIn(touched, field.fieldPath) ? error : '';

                    if (
                      field.fieldPath === 'beneficiary.bank_account.code' ||
                      field.fieldPath === 'beneficiary.bank_name'
                    ) {
                      return (
                        <Autocomplete
                          key={field.fieldPath}
                          name={field.fieldPath}
                          label={label}
                          value={String(fieldValue)}
                          options={bankAutocompleteOptions}
                          disabled={accountNumberDisabled || bankAutocompleteOptions.length === 0}
                          error={Boolean(showError)}
                          helperText={showError || undefined}
                          onBlur={handleBlur}
                          onChange={event => setFieldValue(field.fieldPath, event.target.value || '')}
                          renderOption={(props, option) => (
                            <li {...props} className={`${props.className ?? ''} payout-create__bank-option`}>
                              <span className="payout-create__bank-option-code">{option.value}</span>
                              <span className="payout-create__bank-option-label">{option.label}</span>
                            </li>
                          )}
                        />
                      );
                    }

                    if (field.fieldPath === 'purpose' || field.fieldPath === 'beneficiary.purpose') {
                      return null;
                    }

                    if (
                      field.fieldPath === 'currency_to_pay' ||
                      field.fieldPath === 'beneficiary.bank_account.currency'
                    ) {
                      return (
                        <PayoutCurrencySelect
                          key={field.fieldPath}
                          name={field.fieldPath}
                          value={String(fieldValue)}
                          transferCountry={country}
                          currencyOptions={currencyOptions}
                          defaultOptionText={l('payouts.create.field.selectPlaceholder')}
                          error={showError}
                          onBlur={handleBlur}
                          onChange={currency => setFieldValue(field.fieldPath, currency)}
                        />
                      );
                    }

                    if (
                      field.fieldPath === 'beneficiary.document.type' &&
                      country &&
                      documentTypeSelectOptions.length > 0
                    ) {
                      return (
                        <SelectMenu
                          key={field.fieldPath}
                          name={field.fieldPath}
                          label={label}
                          optionsArray={documentTypeSelectOptions}
                          value={fieldValue as string}
                          error={showError}
                          onBlur={handleBlur}
                          onChange={event => setFieldValue(field.fieldPath, event.target.value)}
                        />
                      );
                    }

                    if (
                      field.fieldPath === 'beneficiary.relationship' &&
                      relationshipSelectOptions.length > 0
                    ) {
                      return (
                        <SelectMenu
                          key={field.fieldPath}
                          name={field.fieldPath}
                          label={label}
                          optionsArray={relationshipSelectOptions}
                          value={fieldValue as string}
                          error={showError}
                          onBlur={handleBlur}
                          onChange={event => setFieldValue(field.fieldPath, event.target.value)}
                        />
                      );
                    }

                    if (
                      field.fieldPath === 'beneficiary.bank_account.type' &&
                      country &&
                      bankAccountTypeSelectOptions.length > 0
                    ) {
                      return (
                        <SelectMenu
                          key={field.fieldPath}
                          name={field.fieldPath}
                          label={label}
                          optionsArray={bankAccountTypeSelectOptions}
                          value={fieldValue as string}
                          error={showError}
                          onBlur={handleBlur}
                          onChange={event => setFieldValue(field.fieldPath, event.target.value)}
                        />
                      );
                    }

                    if (field.fieldPath === 'remitter.nationality' && countrySelectOptions.length > 0) {
                      return (
                        <SelectMenu
                          key={field.fieldPath}
                          name={field.fieldPath}
                          label={label}
                          optionsArray={countrySelectOptions}
                          value={fieldValue as string}
                          error={showError}
                          onBlur={handleBlur}
                          onChange={event => setFieldValue(field.fieldPath, event.target.value)}
                        />
                      );
                    }

                    if (field.fieldPath === 'beneficiary.email') {
                      return (
                        <Input
                          key={field.fieldPath}
                          name={field.fieldPath}
                          label={label}
                          type="email"
                          value={fieldValue as string}
                          error={showError}
                          shrink={Boolean(fieldValue) || Boolean(getIn(touched, field.fieldPath))}
                          onFocus={() => undefined}
                          onBlur={handleBlur}
                          onChange={event => setFieldValue(field.fieldPath, event.target.value)}
                        />
                      );
                    }

                    return (
                      <Input
                        key={field.fieldPath}
                        type={field.dataType === 'NUMBER' ? 'number' : field.dataType === 'DATE' ? 'date' : 'text'}
                        name={field.fieldPath}
                        label={label}
                        value={fieldValue}
                        maxLength={field.maxLength}
                        error={showError}
                        shrink={Boolean(fieldValue) || Boolean(getIn(touched, field.fieldPath))}
                        disabled={accountNumberDisabled && field.fieldPath === 'beneficiary.bank_account.account'}
                        onFocus={() => undefined}
                        onBlur={handleBlur}
                        onChange={event => setFieldValue(field.fieldPath, event.target.value)}
                      />
                    );
                  })}

                <div className="payout-create__buttons">
                  <Button
                    variant="contained"
                    size="large"
                    onClick={onBack}
                    sx={{ backgroundColor: '#E8E8E8', color: 'black', ':hover': { backgroundColor: '#D0D0D0' } }}
                  >
                    {l('common.cancel')}
                  </Button>
                  <Button
                    variant="contained"
                    size="large"
                    type="button"
                    disabled={loading || validating}
                    onClick={() => handleSubmit()}
                  >
                    {submitLabel}
                  </Button>
                </div>
              </form>
            )}
          </Formik>
        </div>
      </Card>
    </div>
  );
};

export default BankAccountForm;
